import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";

/**
 * Phase 8.1 — /verify-email
 *
 * Consumes the single-use token from the query string.
 *
 * Feb 2026 — Phase E-Prep-4c completion UX:
 *
 *   Two success paths, chosen by whether the visitor already holds a
 *   valid Rostr session cookie:
 *
 *     • authenticated — the mail was opened in the SAME browser that
 *       just signed up. We refresh the auth session so the app gate
 *       drops, briefly show a "Je wordt doorgestuurd naar Rostr…"
 *       confirmation, and forward to `/` (RoleLanding will pick the
 *       correct authenticated home per role).
 *
 *     • unauthenticated — the mail was opened somewhere else
 *       (different browser, incognito, another device). Verification
 *       still succeeds on the backend; we simply cannot log the user
 *       in from a verification token, so we show a plain success
 *       screen with a single "Ga naar Rostr" CTA that hands off to the
 *       normal /login flow.
 *
 *   The verification token is NEVER exchanged for an access/refresh
 *   cookie. `POST /api/auth/verify-email` only ever returns
 *   `{ok, email_verified}` and mints no session; the session, if
 *   present, was already established by the user's original signup /
 *   login through the standard auth endpoints.
 *
 *   The duplicate-submit guards from Phase E-Prep-4b remain in place:
 *   `firedRef` prevents a same-instance double-fire, and on any 400
 *   from the verify endpoint we reconcile with the existing
 *   authenticated `GET /auth/verification-status` endpoint. Only a
 *   positive server-side confirmation flips us to success; anything
 *   else preserves the invalid/expired copy.
 */
const AUTHED_REDIRECT_MS = 1500;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | invalid
  const token = params.get("token");
  const firedRef = useRef(false);
  const mountedRef = useRef(true);
  const redirectedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    // Guard 1 — same-instance double-fire (StrictMode / Safari prefetch).
    if (firedRef.current) return;
    firedRef.current = true;

    async function run() {
      if (!token) {
        setState("invalid");
        return;
      }
      // Scrub the token from the URL immediately.
      try {
        window.history.replaceState({}, document.title, "/verify-email");
      } catch { /* jsdom safety */ }
      try {
        await api.post("/auth/verify-email", { token });
        if (!mountedRef.current) return;
        setState("success");
        // Try to hydrate the session. If the visitor has valid cookies
        // this populates `user` and the redirect effect below fires.
        // If not (mail opened in another browser), it fails silently
        // and we stay on the unauthenticated success screen.
        try { await refreshUser(); } catch { /* ignore */ }
      } catch (e) {
        if (!mountedRef.current) return;
        // Guard 2 — reconcile with server-side truth.
        try {
          const { data: status } = await api.get("/auth/verification-status");
          if (!mountedRef.current) return;
          if (status?.email_verified === true) {
            setState("success");
            try { await refreshUser(); } catch { /* ignore */ }
            return;
          }
        } catch { /* unauthenticated / network — stay on invalid */ }
        setState("invalid");
      }
    }
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    // Real-unmount guard for mountedRef.
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    // Auto-redirect to the role-appropriate home once success is set
    // AND the auth session actually exists. `RoleLanding` (App.js)
    // owns the role → route mapping; we intentionally do NOT hardcode
    // that decision here.
    if (state !== "success" || !user || redirectedRef.current) return;
    redirectedRef.current = true;
    const id = setTimeout(() => {
      if (mountedRef.current) navigate("/", { replace: true });
    }, AUTHED_REDIRECT_MS);
    return () => clearTimeout(id);
  }, [state, user, navigate]);

  const goToLogin = () => navigate("/login", { replace: true });

  return (
    <div
      className="min-h-screen grid place-items-center px-4 py-10 bg-[hsl(var(--bg))]"
      data-testid="verify-email-page"
    >
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="card p-7 max-w-md w-full"
      >
        <div className="eyebrow mb-2">Rostr</div>

        {state === "verifying" && (
          <div data-testid="verify-email-verifying" className="flex flex-col items-start gap-3">
            <Loader2 className="animate-spin" size={22} />
            <h1 className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))]">
              {t("verify_email_verifying")}
            </h1>
            <p className="text-[14px] text-[hsl(var(--text-muted))]">
              {t("verify_email_check_body")}
            </p>
          </div>
        )}

        {state === "success" && (
          <div data-testid="verify-email-success" className="flex flex-col items-start gap-3">
            <CheckCircle2 size={26} className="text-[hsl(142_71%_45%)]" />
            <h1 className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))]">
              {t("verify_email_success")}
            </h1>
            {user ? (
              <>
                <p
                  className="text-[14px] text-[hsl(var(--text-muted))]"
                  data-testid="verify-email-success-body-authed"
                >
                  {t("verify_email_success_body_authed")}
                </p>
                <div className="inline-flex items-center gap-2 text-[13px] text-[hsl(var(--text-muted))] mt-1">
                  <Loader2 className="animate-spin" size={14} aria-hidden />
                  <span>{t("verify_email_redirecting")}</span>
                </div>
              </>
            ) : (
              <>
                <p
                  className="text-[14px] text-[hsl(var(--text-muted))]"
                  data-testid="verify-email-success-body-unauth"
                >
                  {t("verify_email_success_body_unauth")}
                </p>
                <button
                  onClick={goToLogin}
                  data-testid="verify-email-continue-to-login"
                  className="btn btn-primary mt-2"
                >
                  {t("verify_email_continue_to_rostr")}
                </button>
              </>
            )}
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="verify-email-invalid" className="flex flex-col items-start gap-3">
            <XCircle size={26} className="text-[hsl(var(--danger))]" />
            <h1 className="font-display text-[22px] tracking-tight text-[hsl(var(--text-strong))]">
              {t("verify_email_invalid")}
            </h1>
            <p className="text-[14px] text-[hsl(var(--text-muted))]">
              {t("verify_email_invalid_body")}
            </p>
            {user ? (
              <button
                onClick={() => navigate("/", { replace: true })}
                data-testid="verify-email-back-to-gate"
                className="btn btn-primary mt-2"
              >
                {t("verify_email_back_to_resend")}
              </button>
            ) : (
              <button
                onClick={() => navigate("/login", { replace: true })}
                data-testid="verify-email-back-to-login"
                className="btn btn-primary mt-2"
              >
                {t("verify_email_back_to_login")}
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
